import { Stack, useMantineTheme } from '@mantine/core';
import type { TFunction } from 'i18next';
import { observable, reaction, toJS, when } from 'mobx';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import type { IImportColumn, Importer } from '@feathr/blackbox';
import { hasFileColumn, isWaitingForColumn } from '@feathr/blackbox';
import { Alert, Button, type ButtonType, ButtonValid, Form, Spinner } from '@feathr/components';
import { useFlags, useStore } from '@feathr/extender/state';
import { isImportsRevampEnabled } from '@feathr/extender/utils/importsRevampFlags';
import { DEFAULT_DEBOUNCE_WAIT } from '@feathr/hooks';

import { EXTERNAL_ID_HELP_URL } from '../DataImportsPage.utils';
import DataImportColumnConfig from './DataImportColumnConfig';
import DataImportPreview from './DataImportPreview';
import { useMappingPreview } from './DataImportStepTwo.hooks';
import { getUnusedColumns } from './DataImportStepTwo.utils';
import ImportMappingTable from './ImportMappingTable';

interface IProps {
  disabled?: boolean;
  onPrev: () => void;
  onNext: () => void;
  importer: Importer;
  /** False when the page renders step navigation in its own action bar. */
  showActions?: boolean;
}

interface IButtonProps {
  onNext: () => void;
  importer: Importer;
  /** Button copy. Defaults to "Next"; the revamped wizard's action bar says "Continue". */
  label?: string;
  type?: ButtonType;
}

export const validateStepTwo = (importer: Importer, t: TFunction) => {
  function validateColumn(column: IImportColumn): string | undefined {
    const options = { columnName: column.column_name, interpolation: { escapeValue: false } };
    if (!column.feathr_attr) {
      return t('{{columnName}} must map to a field.', options);
    }
    if (!column.attr_type) {
      return t('Field for "{{columnName}}" needs a data type.', options);
    }

    if (column?.u_key === '') {
      return t('Custom field mapped to "{{columnName}}" needs a name.', options);
    }
    return undefined;
  }

  const mappers: IImportColumn[] = importer.get('column_mappers');
  const hasEmail = mappers.some((m) => m.feathr_attr === 'email');
  const errors = toJS(importer.validate(['column_mappers']).errors);
  if (!hasEmail) {
    errors.push(t('Exactly one column must map to the Email field.'));
  }
  if (mappers.some((mapper) => isWaitingForColumn(mapper))) {
    errors.push(t('Each row needs a column from your file. Select one or remove the row.'));
  }
  return [
    ...errors,
    ...(mappers
      .filter(hasFileColumn)
      .map(validateColumn)
      .filter((error?: string) => !!error) as string[]),
  ];
};

export const StepTwoNextButton = observer(({ onNext, importer, label, type }: IButtonProps) => {
  const { CustomFields } = useStore();
  const [fieldExists, setFieldExists] = useState<string[]>([]);
  const { t } = useTranslation();

  const validationErrors = validateStepTwo(importer, t);
  fieldExists.forEach((entry) => validationErrors.push(entry));

  const defaults = ['email'];

  // Also checks on mount: a reopened draft's new field may have a name another field took since.
  useEffect(() => {
    // A lookup can resolve after the names it checked have changed.
    let latestCheck = 0;

    const dispose = reaction(
      () => importer.getEphemeralCustomFields(CustomFields).map((cf) => cf.get('u_key')),
      async (fieldNames) => {
        const checkId = ++latestCheck;
        if (!fieldNames.length) {
          setFieldExists([]);
          return;
        }
        const existingFields = CustomFields.list({
          filters: {
            u_key__in: fieldNames,
            is_archived__ne: true,
            collection: 'Person',
          },
        });
        await when(() => !existingFields.isPending);
        if (checkId !== latestCheck) {
          return;
        }
        const builtinFieldErrs = fieldNames
          .filter((fn) => defaults.includes(fn?.toLowerCase()))
          .map((fn) =>
            t('{{fieldName}} is a built-in field. Use the built in field instead.', {
              fieldName: fn,
            }),
          );
        const existingFieldErrs = existingFields.models.map((f) =>
          t(
            '{{fieldName}} is a custom field that already exists. Choose a different name or use the existing field.',
            { fieldName: f.get('u_key') },
          ),
        );
        setFieldExists([...builtinFieldErrs, ...existingFieldErrs]);
      },
      { delay: DEFAULT_DEBOUNCE_WAIT, fireImmediately: true },
    );

    return (): void => {
      latestCheck += 1;
      dispose();
    };
  }, []);

  return (
    <ButtonValid errors={validationErrors} name={'next_step'} onClick={onNext} type={type}>
      {label ?? t('Next')}
    </ButtonValid>
  );
});

function DataImportStepTwo({
  disabled = false,
  importer,
  onNext,
  onPrev,
  showActions = true,
}: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const isRevampEnabled = isImportsRevampEnabled(useFlags());
  const theme = useMantineTheme();
  const preview = useMappingPreview(importer, disabled);

  function addColumn(): void {
    const [name] = getUnusedColumns(
      importer.get('column_names') ?? [],
      importer.get('column_mappers'),
    );
    if (name === undefined) {
      return;
    }
    const obj = observable({
      column_name: name,
      feathr_attr: undefined,
      attr_type: undefined,
    } as IImportColumn);
    importer.get('column_mappers').push(obj);
  }

  const addColumnButton = (
    <Button
      disabled={
        disabled || importer.get('column_names').length === importer.get('column_mappers').length
      }
      key={'add'}
      onClick={addColumn}
    >
      {t('Add column')}
    </Button>
  );

  // The revamped step adds columns from the mapping table's footer instead.
  const legacyActions = showActions
    ? [
        <Button key={'prev'} onClick={onPrev}>
          {t('Previous')}
        </Button>,
        addColumnButton,
        <StepTwoNextButton importer={importer} key={'next'} onNext={onNext} />,
      ]
    : [addColumnButton];

  const columnMappers = importer.get('column_mappers').filter(hasFileColumn);

  const columnRows = columnMappers.map((column, index) => (
    <DataImportColumnConfig
      column={column}
      disabled={disabled}
      importer={importer}
      index={index}
      key={column.column_name}
    />
  ));

  const previewFailureAlert =
    isRevampEnabled && preview.failure ? (
      <Alert
        description={
          preview.failure.reason ??
          (preview.failure.isRetryable
            ? t("Try again. If the preview still doesn't load, contact support.")
            : t('Contact support if you need help.'))
        }
        name={'import_preview_failed'}
        title={t("Couldn't load the preview")}
        type={'danger'}
      >
        {preview.failure.isRetryable && (
          <Button name={'retry_import_preview'} onClick={preview.retry} type={'secondary'}>
            {t('Try again')}
          </Button>
        )}
      </Alert>
    ) : undefined;

  return (
    <>
      <Form
        actions={isRevampEnabled ? undefined : legacyActions}
        description={
          // The revamped step's mapping card carries the description.
          isRevampEnabled ? undefined : (
            <Trans t={t}>
              <p>Map the columns from your spreadsheet to fields in Feathr.</p>
              <p>
                In order for your data to be properly imported into Feathr, each column of your
                spreadsheet must be mapped to a field on every person that is being imported. You
                can map each column to a field that already exists, or to a new custom field you
                create.
              </p>
              <p>
                Note that External ID and Email Address will be used to find existing records. If an
                existing record is found, its content will be overwritten by the import. For this
                reason, these fields should be unique.
              </p>
            </Trans>
          )
        }
        label={isRevampEnabled ? t('Map columns') : t('Map')}
        width={isRevampEnabled ? 'wide' : 'narrow'}
      >
        {isRevampEnabled ? (
          <Stack gap={theme.spacing['4']}>
            <Alert
              description={t(
                "Rows with an External ID update the person who has that ID. If no one has it, they update the person with a matching email, as long as that person doesn't have an External ID yet. If the email belongs to someone with a different External ID, a new person is created.",
              )}
              title={t('Important: when mapping External ID')}
              type={'info'}
            >
              <a href={EXTERNAL_ID_HELP_URL} rel={'noopener noreferrer'} target={'_blank'}>
                {t('Learn more about External ID')}
              </a>
            </Alert>
            <ImportMappingTable
              cleaned={preview.cleaned}
              disabled={disabled}
              importer={importer}
              samples={preview.samples}
              unreadable={preview.unreadable}
            />
          </Stack>
        ) : (
          columnRows
        )}
      </Form>
      {!isRevampEnabled && preview.failure && (
        <Alert
          description={preview.failure.message}
          title={t("Couldn't load the preview")}
          type={'danger'}
        />
      )}
      {preview.rows && columnMappers ? (
        <DataImportPreview
          columnMappers={columnMappers}
          failure={previewFailureAlert}
          isRevampEnabled={isRevampEnabled}
          rows={preview.rows}
          tagIds={importer.get('tag_ids', [])}
        />
      ) : (
        <Spinner />
      )}
    </>
  );
}

export default observer(DataImportStepTwo);
